import apiClient from "../lib/apiClients";
import {
  CourtApplication,
  CourtApplicationFeedback,
  CourtApplicationRespondBack,
  Execution,
  Remand,
} from "types";
import { CourtMarks, withCourtMarks } from "components/CourtFilingMarks";

const BASE = "/crime_case_management";

// Build multipart FormData from a plain object. File values (and arrays of
// files, e.g. multiple execution_file) are appended with repeated keys; other
// scalars are appended as-is. Empty/undefined values and nested collections are
// skipped. The instance default Content-Type is application/json, so multipart
// requests must override the header explicitly (axios sets the boundary for a
// real FormData regardless).
const SKIP_KEYS = ["id", "documents", "feedbacks", "respond_backs", "created_at", "updated_at"];

const buildFormData = (data: Record<string, any>): FormData => {
  const fd = new FormData();
  Object.entries(data || {}).forEach(([key, value]) => {
    if (SKIP_KEYS.includes(key)) return;
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      // Repeated key for multiple files (skip empty entries).
      value.forEach((item) => {
        if (item instanceof File) fd.append(key, item);
      });
      return;
    }
    if (value instanceof File) {
      fd.append(key, value);
      return;
    }
    if (typeof value === "object") return; // skip nested objects
    fd.append(key, String(value));
  });
  return fd;
};

const multipart = { headers: { "Content-Type": "multipart/form-data" } };

const buildQuery = (params?: Record<string, any>) => {
  const qs = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") qs.append(k, String(v));
  });
  const s = qs.toString();
  return s ? `?${s}` : "";
};

export interface CourtCaseListParams {
  case_id?: string;
  status?: string;
  admin_unit_id?: string;
  assigned_prosecutor_id?: string;
}

// ---------------- Court Application ----------------

export const fetchCourtApplications = async (params?: CourtCaseListParams) => {
  const response = await apiClient.get<CourtApplication[]>(
    `${BASE}/court-applications/${buildQuery(params)}`,
  );
  return response.data;
};

export const fetchCourtApplicationById = async (id: string) => {
  const response = await apiClient.get<CourtApplication>(
    `${BASE}/court-applications/${id}/`,
  );
  return response.data;
};

export const addCourtApplication = async (data: CourtApplication) => {
  const response = await apiClient.post(
    `${BASE}/court-applications/`,
    buildFormData(data as Record<string, any>),
    multipart,
  );
  return response.data;
};

// File/content edit (multipart).
export const editCourtApplication = async (data: CourtApplication) => {
  const response = await apiClient.patch(
    `${BASE}/court-applications/${data?.id}/`,
    buildFormData(data as Record<string, any>),
    multipart,
  );
  return response.data;
};

// Workflow advance / simple field PATCH (JSON).
export const updateCourtApplication = async (
  data: Partial<CourtApplication>,
  marks?: CourtMarks,
) => {
  const { body, config } = withCourtMarks(data, marks);
  const response = await apiClient.patch(
    `${BASE}/court-applications/${data?.id}/`,
    body,
    config,
  );
  return response.data;
};

export const addCourtApplicationFeedback = async (
  data: CourtApplicationFeedback,
) => {
  const response = await apiClient.post(
    `${BASE}/court-application-feedbacks/`,
    data,
  );
  return response.data;
};

export const addCourtApplicationRespondBack = async (
  data: CourtApplicationRespondBack,
) => {
  const response = await apiClient.post(
    `${BASE}/court-application-respond-backs/`,
    data,
  );
  return response.data;
};

// ---------------- Remand ----------------

export const fetchRemands = async (params?: CourtCaseListParams) => {
  const response = await apiClient.get<Remand[]>(
    `${BASE}/remands/${buildQuery(params)}`,
  );
  return response.data;
};

export const fetchRemandById = async (id: string) => {
  const response = await apiClient.get<Remand>(`${BASE}/remands/${id}/`);
  return response.data;
};

export const addRemand = async (data: Remand) => {
  const response = await apiClient.post(
    `${BASE}/remands/`,
    buildFormData(data as Record<string, any>),
    multipart,
  );
  return response.data;
};

// Workflow advance / assign prosecutor (JSON).
export const updateRemand = async (data: Partial<Remand>) => {
  const response = await apiClient.patch(`${BASE}/remands/${data?.id}/`, data);
  return response.data;
};

// File/content edit (multipart).
export const editRemand = async (data: Remand) => {
  const response = await apiClient.patch(
    `${BASE}/remands/${data?.id}/`,
    buildFormData(data as Record<string, any>),
    multipart,
  );
  return response.data;
};

// ---------------- Execution ----------------

export const fetchExecutions = async (params?: CourtCaseListParams) => {
  const response = await apiClient.get<Execution[]>(
    `${BASE}/executions/${buildQuery(params)}`,
  );
  return response.data;
};

export const fetchExecutionById = async (id: string) => {
  const response = await apiClient.get<Execution>(`${BASE}/executions/${id}/`);
  return response.data;
};

// data may include execution_file as a File[] for multiple files.
export const addExecution = async (data: Record<string, any>) => {
  const response = await apiClient.post(
    `${BASE}/executions/`,
    buildFormData(data),
    multipart,
  );
  return response.data;
};

export const editExecution = async (data: Record<string, any>) => {
  const response = await apiClient.patch(
    `${BASE}/executions/${data?.id}/`,
    buildFormData(data),
    multipart,
  );
  return response.data;
};
